import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
/**
 * Interface for metadata field configuration
 */
export interface MetadataFieldConfig {
  key: string;
  label: string;
  type: 'text' | 'url' | 'array' | 'date' | 'country' | 'brandName' | 'download' | 'document' | 'explanation';
  displayInCompact: boolean;
  displayInDetailed: boolean;
  required?: boolean;
  displayOrder: number;
  formatter?: string; // For special formatting requirements
  moduleRestrictions?: string[]; // Optional: restrict to specific modules
}

/**
 * Interface for universal field configuration
 */
export interface UniversalFieldConfig {
  label: string;
  type: 'text' | 'url' | 'array' | 'date' | 'country' | 'brandName' | 'download' | 'document' | 'explanation';
  displayInCompact: boolean;
  displayInDetailed: boolean;
  required?: boolean;
  displayOrder: number;
  formatter?: string;
  moduleRestrictions?: string[]; // Optional: only show for specific modules
  aliases?: string[]; // Alternative key names that map to this field
}

/**
 * Interface for module configuration
 */
export interface ModuleConfig {
  moduleName: string;
  moduleDisplayName: string;
  urlTemplate?: string; // Template for generating view URLs
  idField: string; // Field to use as ID for this module
}

/**
 * Centralized metadata configuration service
 * Uses universal key-based configuration for easy management
 */
@Injectable({
  providedIn: 'root'
})
export class MetadataConfigService {

  // Universal field configuration - all keys in one place for easy management
  private readonly universalFieldConfig: Record<string, UniversalFieldConfig> = {
    // ID fields
    'id': {
      label: this.translate.instant('ID'),
      type: 'text',
      displayInCompact: true,
      displayInDetailed: false, // Hide in detailed view for all modules
      required: true,
      displayOrder: 1,
      moduleRestrictions: [] // Keep for compact view compatibility
    },
    'ins_id': {
      label: 'Regulation ID',
      type: 'text',
      displayInCompact: true,
      displayInDetailed: false,
      required: true,
      displayOrder: 1,
      moduleRestrictions: ['regulations'] // Only show for regulations module
    },
    'rtq_id': {
      label: this.translate.instant('RTQ_ID'),
      type: 'text',
      displayInCompact: true,
      displayInDetailed: false, // Hide in detailed view as it's redundant with ID
      required: true,
      displayOrder: 1,
      moduleRestrictions: ['rtq'] // Only show for RTQ module in compact view
    },

    // Title fields - Do not display in detailed view as they're already shown in the header
    'metadata.title': {
      label: this.translate.instant('TITLE'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false,
      required: true,
      displayOrder: 2,
      aliases: ['title', 'insight_title', 'metadata.insight_title']
    },
    'metadata.query_title': {
      label: this.translate.instant('QUERY_TITLE'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false,
      required: true,
      displayOrder: 2,
      moduleRestrictions: [],
      aliases: ['query_title']
    },

    // Long-form body copy — hide in detailed view (header + docs/refs are sufficient)
    'metadata.synopsis': {
      label: 'Synopsis',
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false,
      displayOrder: 2.1,
      aliases: ['synopsis']
    },
    'metadata.description': {
      label: 'Description',
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false,
      displayOrder: 2.2,
      aliases: ['description']
    },

    // Brand Name fields
    'metadata.brandNames': {
      label: 'Brand Name',
      type: 'brandName',
      displayInCompact: false, // Handled in brand name section
      displayInDetailed: false, // Handled in brand name section
      displayOrder: 2.5,
      aliases: ['brandNames', 'brandName', 'metadata.brandName']
    },

    // Country fields
    'metadata.countries': {
      label: this.translate.instant('COUNTRY'),
      type: 'country',
      displayInCompact: false, // Handled in location section
      displayInDetailed: false, // Handled in location section
      displayOrder: 3,
      aliases: ['countries', 'Country', 'metadata.Country']
    },

    // Category fields
    'metadata.categories': {
      label: 'Categories',
      type: 'array',
      displayInCompact: false, // Handled in dedicated categories section
      displayInDetailed: false, // Handled in dedicated categories section
      displayOrder: 3.5,
      aliases: ['categories']
    },

    // Reference and URL fields
    'metadata.ref_url': {
      label: this.translate.instant('REFERENCES_TITLE_TEXT'),
      type: 'url',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 10,
      aliases: ['ref_url', 'insight_ref_urls', 'metadata.insight_ref_urls']
    },

    // Document fields (prefer documents[] when present; see getFieldsForDisplay)
    'metadata.documents': {
      label: this.translate.instant('DOCUMENTS_TITLE_TEXT'),
      type: 'document',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 15,
      aliases: ['documents']
    },
    'metadata.attachment_name': {
      label: this.translate.instant('DOCUMENTS_TITLE_TEXT'),
      type: 'document',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 15,
      aliases: ['attachment_name']
    },

    // Additional regulation fields - internal identifiers, never rendered
    'metadata.encrypted_regulation_id': {
      label: this.translate.instant('ENCRYPTED_ID'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false,
      displayOrder: 4,
      moduleRestrictions: [],
      aliases: ['encrypted_regulation_id']
    },
    'metadata.reference_insight_id': {
      label: this.translate.instant('Reference Insight ID'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false,
      displayOrder: 5,
      moduleRestrictions: [],
      aliases: ['reference_insight_id']
    },
    'metadata.insight_version': {
      label: 'Insight Version',
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false,
      displayOrder: 5.5,
      moduleRestrictions: [],
      aliases: ['insight_version']
    },
    'metadata.version': {
      label: this.translate.instant('DOC_TABLE_VERSION'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 16,
      aliases: ['version']
    },

    // Date fields - marked as not displaying per requirement
    'metadata.release_date': {
      label: 'Release Date',
      type: 'date',
      displayInCompact: true,
      displayInDetailed: true,
      displayOrder: 20,
      aliases: ['release_date']
    },

    // Product ID fields - hide from RTQ detailed view
    'product_id': {
      label: this.translate.instant('PRODUCT_ID'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false, // Hide from all detailed views
      displayOrder: 5,
      moduleRestrictions: []
    },
    'metadata.product_id': {
      label: this.translate.instant('PRODUCT_ID'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false, // Hide from all detailed views
      displayOrder: 5,
      moduleRestrictions: []
    },

    // RTQ specific fields
    'metadata.product_name': {
      label: this.translate.instant('PRODUCT_NAME'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 6,
      moduleRestrictions: ['rtq']
    },
    'metadata.authority': {
      label: this.translate.instant('AUTHORITY'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 7,
      moduleRestrictions: ['rtq']
    },
    'metadata.country_group': {
      label: this.translate.instant('COUNTRY'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 8,
      moduleRestrictions: ['rtq']
    },
    'metadata.document_id': {
      label: this.translate.instant('DOCUMENT_ID'),
      type: 'download',
      displayInCompact: false,
      displayInDetailed: true,
      displayOrder: 9,
      moduleRestrictions: ['rtq']
    },

    // Common fields
    'relevance_score': {
      label: this.translate.instant('RELEVANCE_SPAN'),
      type: 'text',
      displayInCompact: false,
      displayInDetailed: false, // Handled separately in template
      displayOrder: 100
    },
    'explanation': {
      label: this.translate.instant('EXPLANATION'),
      type: 'explanation',
      displayInCompact: false,
      displayInDetailed: false, // Handled separately in template
      displayOrder: 101
    }
  };

  // Module configurations
  private readonly moduleConfigs: Record<string, ModuleConfig> = {
    'regulations': {
      moduleName: 'regulations',
      moduleDisplayName: 'Regulations',
      urlTemplate: '/regulations?modulePath=/Insights/view-regulation?tab=true&insid={id}',
      idField: 'ins_id'
    },
    'rtq': {
      moduleName: 'rtq',
      moduleDisplayName: 'RTQ',
      urlTemplate: '/rtq?modulePath=/rtqs/view-rtq&id={id}',
      idField: 'rtq_id'
    }
  };

  constructor(private readonly translate: TranslateService) { }

  /**
   * Get universal field configuration by key
   */
  getFieldConfig(key: string): UniversalFieldConfig | null {
    // Check direct key match
    if (this.universalFieldConfig[key]) {
      return this.universalFieldConfig[key];
    }

    // Check aliases
    for (const [configKey, config] of Object.entries(this.universalFieldConfig)) {
      if (config.aliases && config.aliases.includes(key)) {
        return config;
      }
    }

    return null;
  }

  /**
   * Get all available field keys
   */
  getAllFieldKeys(): string[] {
    const keys = Object.keys(this.universalFieldConfig);
    // Add aliases
    Object.values(this.universalFieldConfig).forEach(config => {
      if (config.aliases) {
        keys.push(...config.aliases);
      }
    });
    return [...new Set(keys)]; // Remove duplicates
  }

  /**
   * Get module configuration
   */
  getModuleConfig(moduleName: string): ModuleConfig | null {
    return this.moduleConfigs[moduleName] || null;
  }

  /**
   * Add or update a field configuration
   */
  addOrUpdateFieldConfig(key: string, config: UniversalFieldConfig): void {
    this.universalFieldConfig[key] = config;
  }

  /**
   * Add or update a module configuration
   */
  addOrUpdateModuleConfig(moduleName: string, config: ModuleConfig): void {
    this.moduleConfigs[moduleName] = config;
  }

  /**
   * Get fields for display in a specific context, optionally filtered by module
   */
  getFieldsForDisplay(item: any, displayContext: 'compact' | 'detailed', moduleName?: string): MetadataFieldConfig[] {
    const fields: MetadataFieldConfig[] = [];
    const suppressAttachmentName = this.hasUsableDocumentsField(item);

    // Get all available keys from the item
    const availableKeys = this.extractAllKeysFromItem(item);

    for (const key of availableKeys) {
      const config = this.getFieldConfig(key);
      if (!config) continue;

      // Prefer metadata.documents over attachment_name when new schema is present
      if (suppressAttachmentName && this.isAttachmentNameFieldKey(key)) {
        continue;
      }

      // Check if field should be displayed in this context
      const shouldDisplay = displayContext === 'compact' ? config.displayInCompact : config.displayInDetailed;
      if (!shouldDisplay) continue;

      // Check module restrictions - only restrict if field has moduleRestrictions AND doesn't match current module
      if (config.moduleRestrictions && config.moduleRestrictions.length > 0 && moduleName && !config.moduleRestrictions.includes(moduleName)) {
        continue;
      }

      // Check if field has value
      if (!this.hasFieldValue(item, key)) continue;

      fields.push({
        key,
        label: config.label,
        type: config.type,
        displayInCompact: config.displayInCompact,
        displayInDetailed: config.displayInDetailed,
        required: config.required,
        displayOrder: config.displayOrder,
        formatter: config.formatter
      });
    }

    return fields.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Discover all available fields from an item using universal configuration
   */
  discoverAvailableFields(item: any, displayContext: 'compact' | 'detailed', moduleName?: string): MetadataFieldConfig[] {
    // First get configured fields
    const configuredFields = this.getFieldsForDisplay(item, displayContext, moduleName);
    const suppressAttachmentName = this.hasUsableDocumentsField(item);
    
    // Then discover dynamic fields
    const dynamicFields: MetadataFieldConfig[] = [];
    const availableKeys = this.extractAllKeysFromItem(item);
    let dynamicDisplayOrder = 1000; // Start high to not conflict with configured fields

    for (const key of availableKeys) {
      // Skip if this is a configured field (already handled above)
      if (this.getFieldConfig(key)) continue;

      // Skip excluded fields
      if (this.isExcludedField(key)) continue;

      if (suppressAttachmentName && this.isAttachmentNameFieldKey(key)) {
        continue;
      }

      // Check if field has value
      if (!this.hasFieldValue(item, key)) continue;

      // Determine if should display
      const shouldDisplay = displayContext === 'compact' ? 
        this.shouldShowInCompact(key) : 
        this.shouldShowInDetailed(key);
      
      if (!shouldDisplay) continue;

      // Create dynamic field config
      dynamicFields.push({
        key,
        label: this.formatFieldLabel(key),
        type: this.inferFieldType(key, this.getFieldValue(item, key)),
        displayInCompact: displayContext === 'compact' && this.shouldShowInCompact(key),
        displayInDetailed: displayContext === 'detailed' && this.shouldShowInDetailed(key),
        displayOrder: dynamicDisplayOrder++
      });
    }

    // Combine configured and dynamic fields
    return [...configuredFields, ...dynamicFields].sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Extract all possible keys from an item (including nested)
   */
  private extractAllKeysFromItem(item: any, prefix: string = ''): string[] {
    const keys: string[] = [];
    
    if (!item || typeof item !== 'object') return keys;

    Object.keys(item).forEach(key => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      keys.push(fullKey);

      const value = item[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        keys.push(...this.extractAllKeysFromItem(value, fullKey));
      }
    });

    return keys;
  }

  /**
   * Check if a field should be excluded from dynamic discovery
   */
  private isExcludedField(key: string): boolean {
    const excludedFields = [
      'id', 'ins_id', 'rtq_id', 'product_id', 'metadata.product_id', 'key', 'original_key', 'index',
      'title', 'insight_title', 'query_title', 'metadata.title', 'metadata.insight_title', 'metadata.query_title',
      'synopsis', 'description', 'metadata.synopsis', 'metadata.description',
      'relevance_score', 'explanation', 'reasoning', 'contribution_level', 'module_name', 'metadata',
      'insight_markets', 'metadata.insight_markets', 'insight_id', 'metadata.insight_id',
      'document_regulator_version', 'metadata.document_regulator_version', 'document_name', 'metadata.document_name',
      'insight_ref_urls', 'metadata.insight_ref_urls'
    ];
    
    // Check exact matches first
    if (excludedFields.includes(key)) {
      return true;
    }
    
    // Check for title-related fields (more comprehensive)
    const keyLower = key.toLowerCase();
    if (keyLower.includes('title') || keyLower === 'title') {
      return true;
    }
    
    return false;
  }

  /**
   * Determine if a field should be shown in compact view (dynamic fields only)
   */
  private shouldShowInCompact(key: string): boolean {
    // For dynamic fields, very restrictive compact view
    return false; // Only configured fields should show in compact view
  }

  /**
   * Determine if a field should be shown in detailed view (dynamic fields only)
   */
  private shouldShowInDetailed(key: string): boolean {
    const keyLower = key.toLowerCase();
    
    // Exclude countries / markets (handled in location section) and dates (release_date uses dedicated row)
    if (keyLower.includes('country') || keyLower.includes('countries') || keyLower.includes('insight_markets')) {
      return false;
    }
    if (keyLower.includes('date') || keyLower.includes('release_date')) return false;

    const lastSegment = keyLower.split('.').pop() ?? '';
    if (lastSegment === 'synopsis' || lastSegment === 'description') return false;
    
    return true;
  }

  /**
   * Get field value from metadata item using dot notation
   */
  getFieldValue(item: any, fieldKey: string): any {
    if (!item || !fieldKey) return null;

    const keys = fieldKey.split('.');
    let value = item;

    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  /**
   * Check if a field has a value in the metadata item
   */
  hasFieldValue(item: any, fieldKey: string): boolean {
    const value = this.getFieldValue(item, fieldKey);
    return this.hasValue(value);
  }

  /**
   * True when metadata.documents / documents has at least one object with doc_name.
   */
  private hasUsableDocumentsField(item: any): boolean {
    const nested = this.getFieldValue(item, 'metadata.documents');
    const flat = nested == null ? this.getFieldValue(item, 'documents') : nested;
    if (!Array.isArray(flat) || flat.length === 0) {
      return false;
    }
    return flat.some(
      (entry) =>
        entry &&
        typeof entry === 'object' &&
        typeof (entry as { doc_name?: unknown }).doc_name === 'string' &&
        ((entry as { doc_name: string }).doc_name).trim().length > 0
    );
  }

  private isAttachmentNameFieldKey(key: string): boolean {
    const keyLower = key.toLowerCase();
    return keyLower === 'metadata.attachment_name' || keyLower === 'attachment_name';
  }

  /**
   * Check if a value should be considered as having content
   */
  private hasValue(value: any): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string' && value.trim() === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && Object.keys(value).length === 0) return false;
    return true;
  }

  /**
   * Generate URL for viewing an item based on its module configuration
   */
  generateViewUrl(item: any, moduleName: string, baseUrl?: string): string | null {
    const moduleConfig = this.getModuleConfig(moduleName);
    if (!moduleConfig || !moduleConfig.urlTemplate) return null;

    const id = this.getFieldValue(item, moduleConfig.idField);
    if (!id) return null;

    let url = moduleConfig.urlTemplate.replace('{id}', encodeURIComponent(id));
    
    if (baseUrl) {
      url = baseUrl + url;
    }

    return url;
  }

  /**
   * Extract module name from item
   */
  extractModuleName(item: any): string | null {
    return this.getFieldValue(item, 'module_name');
  }

  /**
   * Enhanced field value formatting that handles universal types
   */
  formatFieldValueEnhanced(value: any, field: MetadataFieldConfig | { type: string, formatter?: string }): any {
    if (!value) return value;
  
    // Handle complex objects and arrays first, regardless of field type
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      if (value.toString && typeof value.toString === 'function' && value.toString() !== '[object Object]') {
        return value.toString();
      }
      try {
        // Check for circular references by attempting JSON.stringify
        JSON.stringify(value);
        const entries = Object.entries(value);
        if (entries.length > 0) {
          return entries.map(([key, val]) => `${key}: ${val}`).join(', ');
        }
      } catch (e) {
        // Handle circular reference or other JSON errors
        return '';
      }
      return '';
    }
  
    switch (field.type) {
      case 'date':
        if (typeof value === 'string') {
          try {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              return 'Invalid Date';
            }
            return date.toLocaleDateString('en-US', {
              month: 'short', // e.g., "Dec"
              day: '2-digit', // e.g., "01"
              year: 'numeric' // e.g., "2024"
            });
          } catch {
            return value;
          }
        }
        return value;
  
      case 'country':
        return value; // Already handled above if array
  
      case 'array':
        return value; // Already handled above if array
  
      case 'text':
        if (field.formatter === 'truncate' && typeof value === 'string') {
          const targetText = 'This is a very long text that should be truncated';
          return value.length > targetText.length ? targetText + '...' : value;
        }
        return value;
  
      case 'url':
      case 'document':
      case 'download':
      case 'explanation':
        return value;
  
      default:
        return value;
    }
  }

  /**
   * Format field label from key name
   */
  private formatFieldLabel(key: string): string {
    // Remove common prefixes
    const cleanKey = key.replace(/^metadata\./, '');
    
    return cleanKey
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\./g, ' ') // Replace dots with spaces
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  }

  /**
   * Infer field type based on key name and value
   */
  private inferFieldType(key: string, value: any): 'text' | 'url' | 'array' | 'date' | 'country' | 'download' | 'document' | 'explanation' {
    const keyLower = key.toLowerCase();
    
    if (Array.isArray(value)) {
      if (keyLower.includes('url') || keyLower.includes('ref_url')) {
        return 'url';
      }
      if (keyLower.includes('country') || keyLower.includes('countries')) {
        return 'country';
      }
      if (keyLower.includes('attachment') || keyLower.includes('document')) {
        return 'document';
      }
      return 'array';
    }
    
    if (keyLower.includes('date')) {
      return 'date';
    }
    
    if (keyLower.includes('url') || (typeof value === 'string' && value.startsWith('http'))) {
      return 'url';
    }
    
    if (keyLower.includes('attachment') || keyLower.includes('document')) {
      if (keyLower.includes('name')) {
        return 'document';
      }
      return 'download';
    }
    
    if (keyLower.includes('country') || keyLower.includes('countries')) {
      return 'country';
    }
    
    if (keyLower.includes('explanation')) {
      return 'explanation';
    }
    
    return 'text';
  }

  /**
   * Get all available module names
   */
  getAvailableModuleNames(): string[] {
    return Object.keys(this.moduleConfigs);
  }

  // Legacy compatibility methods (can be deprecated later)
  
  /**
   * @deprecated Use discoverAvailableFields instead
   */
  getFieldsForDisplayWithFallback(item: any, moduleName: string | null, displayContext: 'compact' | 'detailed'): MetadataFieldConfig[] {
    return this.discoverAvailableFields(item, displayContext, moduleName || undefined);
  }

  /**
   * @deprecated Use formatFieldValueEnhanced instead
   */
  formatFieldValue(value: any, field: MetadataFieldConfig): any {
    return this.formatFieldValueEnhanced(value, field);
  }
}